/**
 * Groups a series' episodes into seasons.
 *
 * The API has two shapes: a filled `Seasons` list, each carrying its own
 * `Episodes`, or (what it sends today) an empty `Seasons` and one flat
 * `Episodes` list where each episode has a `SeasonNumber`. Both come out as
 * `[{ number, label, episodes }]`, seasons in order and episodes sorted by
 * `EpisodeNumber`. Every episode is a copy of the raw one with `seasonNumber`
 * and `episodeNumber` added, so existing `ep.Id` / `ep.VideoUrl` reads keep
 * working.
 */

const positiveNumber = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : fallback;
};

const rawEpisodeNumber = (episode) => episode?.EpisodeNumber ?? episode?.episodeNumber;

function numberEpisodes(episodes, seasonNumber) {
  return episodes
    .map((episode, position) => ({ episode, position }))
    .sort(
      (a, b) =>
        positiveNumber(rawEpisodeNumber(a.episode), a.position + 1) -
          positiveNumber(rawEpisodeNumber(b.episode), b.position + 1) || a.position - b.position
    )
    .map(({ episode }, index) => ({
      ...episode,
      seasonNumber,
      episodeNumber: positiveNumber(rawEpisodeNumber(episode), index + 1),
    }));
}

export function buildSeasons(title) {
  if (!title) return [];

  const rawSeasons = title.seasons || title.Seasons;
  if (Array.isArray(rawSeasons) && rawSeasons.length > 0) {
    const given = rawSeasons.map((season) =>
      positiveNumber(
        season.SeasonNumber ?? season.seasonNumber ?? season.Number ?? season.number,
        null
      )
    );
    const taken = new Set(given.filter(Boolean));
    let nextFree = 1;

    return rawSeasons
      .map((season, index) => {
        let number = given[index];
        if (!number) {
          while (taken.has(nextFree)) nextFree += 1;
          number = nextFree;
          taken.add(number);
        }
        return {
          number,
          label: season.Title || season.title || `Season ${number}`,
          episodes: numberEpisodes(season.Episodes || season.episodes || [], number),
        };
      })
      .sort((a, b) => a.number - b.number);
  }

  const rawEpisodes = title.episodes || title.Episodes || [];
  const bySeason = new Map();
  for (const episode of rawEpisodes) {
    const number = positiveNumber(episode?.SeasonNumber ?? episode?.seasonNumber, 1);
    if (!bySeason.has(number)) bySeason.set(number, []);
    bySeason.get(number).push(episode);
  }

  return [...bySeason.entries()]
    .sort(([a], [b]) => a - b)
    .map(([number, episodes]) => ({
      number,
      label: `Season ${number}`,
      episodes: numberEpisodes(episodes, number),
    }));
}

/** Every episode in watch order: season by season, episode by episode. */
export const flattenSeasons = (seasons) => (seasons || []).flatMap((season) => season.episodes);

/** "42m" / "1h 5m" from `Duration` if the API sent text, else `DurationSeconds`. */
export function formatEpisodeDuration(episode) {
  const text = episode?.Duration || episode?.duration;
  if (text) return text;

  const seconds = Number(episode?.DurationSeconds ?? episode?.durationSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;

  const minutes = Math.max(1, Math.round(seconds / 60));
  return minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`;
}
